// src/pages/DashboardHome.jsx
import React, { useEffect, useState } from "react";
import { useAuth } from "../Context/AuthContext";
import { db } from "../firebase/config";
import {
  collection,
  getCountFromServer,
  getDocs,
  getDoc,
  query,
  doc,
  where, // ✅ add
  Timestamp,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import {
  Users,
  CalendarDays,
  Clock4,
  AlertTriangle,
  ChevronRight,
  DollarSign,
} from "lucide-react";
import "../styles/DashboardHome.css";

import { DateTime } from "luxon";

// ✅ NEW imports

function KpiCard({
  icon: Icon,
  label,
  value,
  sub,
  tone = "neutral",
  iconClass = "",
}) {
  return (
    <div className={`db-kpi-card ${tone}`}>
      <div className={`db-kpi-ico ${iconClass}`}>
        <Icon size={18} />
      </div>
      <div className="db-kpi-meta">
        <div className="db-kpi-value">{value}</div>
        <div className="db-kpi-label">{label}</div>
        {sub ? <div className="db-kpi-sub">{sub}</div> : null}
      </div>
    </div>
  );
}

function ListRow({ icon: Icon, text, onClick, iconClass = "" }) {
  return (
    <button className="db-row" type="button" onClick={onClick}>
      <div className="db-row-left">
        <div className={`db-row-ico ${iconClass}`}>
          <Icon size={16} />
        </div>
        <div className="db-row-text">{text}</div>
      </div>
      <ChevronRight size={16} className="db-row-arrow" />
    </button>
  );
}

const toMin = (hhmm = "00:00") => {
  const [h, m] = (hhmm || "00:00").split(":").map((n) => Number(n || 0));
  return h * 60 + m;
};

function buildShiftEnd(dateISO, startTime, stopTime, tz) {
  const baseDay = DateTime.fromISO(dateISO, { zone: tz });

  const [sh, sm] = (startTime || "00:00").split(":").map(Number);
  const [eh, em] = (stopTime || "00:00").split(":").map(Number);

  const start = baseDay.set({
    hour: sh,
    minute: sm,
    second: 0,
    millisecond: 0,
  });
  let end = baseDay.set({ hour: eh, minute: em, second: 0, millisecond: 0 });

  // overnight: end is next day
  if (toMin(stopTime) <= toMin(startTime)) {
    end = end.plus({ days: 1 });
  }

  return { start, end };
}

function findScheduleCoveringDate({ schedules, employeeId, dateISO }) {
  // explicit schedule that covers date
  let schedule = schedules.find(
    (s) =>
      s.employeeId === employeeId &&
      s.weekStartDate <= dateISO &&
      s.weekEndDate >= dateISO,
  );

  // repeatWeekly fallback: most recent schedule before date
  if (!schedule) {
    const prior = schedules
      .filter(
        (s) =>
          s.employeeId === employeeId &&
          s.repeatWeekly &&
          s.weekStartDate <= dateISO,
      )
      .sort((a, b) => b.weekStartDate.localeCompare(a.weekStartDate))[0];

    if (prior) schedule = prior;
  }

  return schedule || null;
}
function computeActivePayPeriodStart({ anchorISO, periodType, tz }) {
  if (!anchorISO) return null;

  const anchor = DateTime.fromISO(anchorISO, { zone: tz }).startOf("day");
  const today = DateTime.now()
    .setZone(tz)
    .startOf("day");
  if (!anchor.isValid) return null;

  // Weekly / Biweekly: fixed-length cycles from anchor
  if (periodType === "weekly" || periodType === "biweekly") {
    const lenDays = periodType === "weekly" ? 7 : 14;
    const diffDays = Math.floor(today.diff(anchor, "days").days);

    if (diffDays < 0) return anchor.toISODate();

    const idx = Math.floor(diffDays / lenDays);
    return anchor.plus({ days: idx * lenDays }).toISODate();
  }

  // Semi-monthly: 1–15 and 16–end (anchor just enables auto mode consistency)
  if (periodType === "semimonthly") {
    if (today.day <= 15) {
      return today.startOf("month").toISODate(); // 1st
    }
    return today
      .set({ day: 16 })
      .startOf("day")
      .toISODate(); // 16th
  }

  return anchor.toISODate();
}

function computePayPeriodEndFromStart({ startISO, periodType, tz }) {
  const start = DateTime.fromISO(startISO, { zone: tz }).startOf("day");
  if (!start.isValid) return null;

  if (periodType === "weekly") return start.plus({ days: 6 }).endOf("day");
  if (periodType === "biweekly") return start.plus({ days: 13 }).endOf("day");

  if (periodType === "semimonthly") {
    if (start.day <= 15) return start.set({ day: 15 }).endOf("day");
    return start.endOf("month");
  }

  return start.endOf("day");
}

function formatRangeLabel({ startISO, endISO, tz }) {
  const s = DateTime.fromISO(startISO, { zone: tz }).startOf("day");
  const e = DateTime.fromISO(endISO, { zone: tz }).startOf("day");
  if (!s.isValid || !e.isValid) return "";

  const sameMonth = s.month === e.month && s.year === e.year;
  return sameMonth
    ? `${s.toFormat("MMM dd")} – ${e.toFormat("dd, yyyy")}`
    : `${s.toFormat("MMM dd, yyyy")} – ${e.toFormat("MMM dd, yyyy")}`;
}

export default function DashboardHome({ onTabChange }) {
  const { user } = useAuth();
  const companyId = user?.companyId;

  // ✅ NEW: total employees state
  const [totalEmployees, setTotalEmployees] = useState(null);
  const [scheduledToday, setScheduledToday] = useState(null);
  const [hoursThisWeek, setHoursThisWeek] = useState(null);
  const [payrollReadyPct, setPayrollReadyPct] = useState(null);
  const [payrollRangeLabel, setPayrollRangeLabel] = useState(""); // ✅ NEW
  const [incompleteProfilesCount, setIncompleteProfilesCount] = useState(null);
  const [newHiresCount, setNewHiresCount] = useState(null);
  const [pendingLeaveCount, setPendingLeaveCount] = useState(null);
  const [pendingExpenseCount, setPendingExpenseCount] = useState(null);
  const [trainingOverdueCount, setTrainingOverdueCount] = useState(null);
  const [taskOverdueCount, setTaskOverdueCount] = useState(0);
  const [taskPendingApprovalCount, setTaskPendingApprovalCount] = useState(0);
  const [clockedInToday, setClockedInToday] = useState(null);

  const [missingClockOutCount, setMissingClockOutCount] = useState(0);
  const [clockInEarlyCount, setClockInEarlyCount] = useState(0);
  const [clockInOnTimeCount, setClockInOnTimeCount] = useState(0);
  const [clockInLateCount, setClockInLateCount] = useState(0);
  const [callOutTodayCount, setCallOutTodayCount] = useState(null);

  const [companyTz, setCompanyTz] = useState("UTC");

  useEffect(() => {
    if (!companyId) return;

    let unsubCompany = null;
    let unsubEmployees = null;
    let unsubSchedules = null;
    let unsubTimeTracking = null;
    let unsubLeave = null;
    let unsubExpenses = null;
    let unsubTasks = null;

    let intervalId = null;

    // Cached data for recompute (so we don’t refetch)
    let tz = "UTC";
    let todayISO = DateTime.now()
      .setZone(tz)
      .toISODate();
    let yesterdayISO = DateTime.now()
      .setZone(tz)
      .minus({ days: 1 })
      .toISODate();

    let employeesCache = []; // [{id,...}]
    let activeEmpIds = new Set();
    let empMap = new Map(); // id -> employee
    let schedulesCache = []; // schedules
    let ttCache = []; // time_tracking rows for today+yesterday
    let tasksCache = []; // recent tasks

    const isProfileIncompleteLocal = (employee) => {
      const requiredFields = [
        "preferredFirstName",
        "phone",
        "gender",
        "address",
        "birthDate",
        "role",
        "jobTitle",
        "group",
        "hireDate",
      ];
      return requiredFields.some((field) => !employee?.[field]);
    };

    const recomputeOperational = () => {
      try {
        const now = DateTime.now().setZone(tz);
        todayISO = now.toISODate();
        yesterdayISO = now.minus({ days: 1 }).toISODate();

        // ---- Scheduled Today ----
        // ---- Scheduled Today ----
        const scheduledEmpIds = new Set();
        let callOutCount = 0;
        const dateISO = todayISO; // ✅ lock value for this run
        const schedules = schedulesCache; // ✅ local ref (optional)

        const getScheduleCovering = (employeeId) => {
          // explicit schedule that covers date
          let schedule = schedules.find(
            (s) =>
              s.employeeId === employeeId &&
              s.weekStartDate <= dateISO &&
              s.weekEndDate >= dateISO,
          );

          // repeatWeekly fallback: most recent schedule before date
          if (!schedule) {
            const prior = schedules
              .filter(
                (s) =>
                  s.employeeId === employeeId &&
                  s.repeatWeekly &&
                  s.weekStartDate <= dateISO,
              )
              .sort((a, b) =>
                b.weekStartDate.localeCompare(a.weekStartDate),
              )[0];

            if (prior) schedule = prior;
          }

          return schedule || null;
        };

        for (const emp of employeesCache.filter(
          (e) => (e.status || "Active") === "Active",
        )) {
          const schedule = getScheduleCovering(emp.id);
          if (!schedule) continue;

          const act = (schedule.activities || schedule.shifts || []).find(
            (a) => a.date === dateISO,
          );
          if (!act) continue;

          // ✅ Count call-outs (do not treat as scheduled coverage)
          if (act.isCallOut) {
            callOutCount += 1;
            continue;
          }

          if (act.isOffDay || act.isHoliday) continue;

          const startTime = act.startTime || schedule.startTime;
          const stopTime = act.stopTime || act.endTime || schedule.stopTime;
          if (!startTime || !stopTime) continue;

          scheduledEmpIds.add(emp.id);
        }

        setScheduledToday(scheduledEmpIds.size);
        setCallOutTodayCount(callOutCount);

        // ---- Currently clocked in + status buckets ----
        let clockedInCount = 0;
        let early = 0;
        let onTime = 0;
        let late = 0;

        // open entries (today + yesterday for overnight)
        const openEntries = [];

        for (const row of ttCache) {
          const isPaidLeave =
            row.entryType === "paid-leave" || row.status === "On Leave";

          if (isPaidLeave) continue;

          // open shift
          if (row.clockIn && !row.clockOut) {
            openEntries.push(row);

            // if it’s for today, count as “currently clocked in today”
            if (row.date === todayISO) {
              const empId = row.userId || row.employeeId;
              if (empId && empMap.has(empId)) {
                clockedInCount += 1;

                const st = String(row.status || "").toLowerCase();
                if (st.includes("early")) early += 1;
                else if (st.includes("late")) late += 1;
                else onTime += 1;
              }
            }
          }
        }

        setClockedInToday(clockedInCount);
        setClockInEarlyCount(early);
        setClockInOnTimeCount(onTime);
        setClockInLateCount(late);

        // ---- Missing clock-out after scheduled end (time-based, so recompute via interval too) ----
        const GRACE_MINUTES = 1;
        const missingEmpIds = new Set();

        for (const entry of openEntries) {
          const empKey = entry.employeeId || entry.userId;
          if (!empKey) continue;
          if (!activeEmpIds.has(empKey)) continue;

          const shiftDateISO = entry.date;

          const schedule = findScheduleCoveringDate({
            schedules: schedulesCache,
            employeeId: empKey,
            dateISO: shiftDateISO,
          });
          if (!schedule) continue;

          const act = (schedule.activities || schedule.shifts || []).find(
            (a) => a.date === shiftDateISO,
          );
          if (!act) continue;
          if (act.isOffDay || act.isHoliday) continue;

          const startTime = act.startTime || schedule.startTime;
          const stopTime = act.stopTime || act.endTime || schedule.stopTime;
          if (!startTime || !stopTime) continue;

          const { end } = buildShiftEnd(shiftDateISO, startTime, stopTime, tz);
          const endWithGrace = end.plus({ minutes: GRACE_MINUTES });

          if (now > endWithGrace) {
            missingEmpIds.add(empKey);
          }
        }

        setMissingClockOutCount(missingEmpIds.size);
      } catch (e) {
        console.error("Operational live recompute failed:", e);
      }
    };

    const recomputeAttentionNeeded = () => {
      try {
        // Incomplete profiles
        const incompleteCount = employeesCache.filter(isProfileIncompleteLocal)
          .length;
        setIncompleteProfilesCount(incompleteCount);

        // Tasks: overdue + pending approval
        const now = DateTime.now().setZone(tz);
        const today = now.startOf("day").toISODate();

        const overdueEmpIds = new Set();
        const pendingEmpIds = new Set();

        for (const t of tasksCache) {
          const status = t.status || "open";
          if (status === "archived") continue;

          const assigneeId =
            t.assigneeId ||
            (Array.isArray(t.assigneeIds) ? t.assigneeIds[0] : null);
          if (!assigneeId) continue;
          if (!activeEmpIds.has(assigneeId)) continue;

          // overdue
          if (t.dueDateISO && t.dueDateISO < today) {
            const review = t.reviewStatus || "not_submitted";
            if (review !== "approved") overdueEmpIds.add(assigneeId);
          }

          // pending approval
          const review = t.reviewStatus || "not_submitted";
          if (review === "submitted") pendingEmpIds.add(assigneeId);
        }

        setTaskOverdueCount(overdueEmpIds.size);
        setTaskPendingApprovalCount(pendingEmpIds.size);
      } catch (e) {
        console.error("Attention Needed live recompute failed:", e);
      }
    };

    // ---- Live listeners ----
    unsubCompany = onSnapshot(doc(db, "companies", companyId), (snap) => {
      const data = snap.data() || {};
      tz = data.timeZone || "UTC";

      setCompanyTz(tz); // ✅ expose tz to JSX

      const now = DateTime.now().setZone(tz);
      todayISO = now.toISODate();
      yesterdayISO = now.minus({ days: 1 }).toISODate();

      recomputeOperational();
      recomputeAttentionNeeded();
    });

    unsubEmployees = onSnapshot(
      collection(db, "companies", companyId, "employees"),
      (snap) => {
        employeesCache = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        activeEmpIds = new Set(
          employeesCache
            .filter((e) => (e.status || "Active") === "Active")
            .map((e) => e.id),
        );

        empMap = new Map();
        employeesCache.forEach((e) => {
          if ((e.status || "Active") !== "Active") return;
          empMap.set(e.id, e);
        });

        recomputeOperational();
        recomputeAttentionNeeded();
      },
    );

    unsubSchedules = onSnapshot(
      collection(db, "companies", companyId, "schedules"),
      (snap) => {
        schedulesCache = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        recomputeOperational();
      },
    );

    // ✅ One listener for today+yesterday using `in`
    unsubTimeTracking = onSnapshot(
      query(
        collection(db, "companies", companyId, "time_tracking"),
        where("date", "in", [todayISO, yesterdayISO]),
      ),
      (snap) => {
        ttCache = snap.docs.map((d) => d.data() || {});
        recomputeOperational();
      },
    );

    // Attention Needed: pending leave
    unsubLeave = onSnapshot(
      query(
        collection(db, "companies", companyId, "leave_requests"),
        where("status", "==", "Pending"),
      ),
      (snap) => {
        setPendingLeaveCount(snap.size);
      },
    );

    // Attention Needed: pending expenses
    unsubExpenses = onSnapshot(
      query(
        collection(db, "companies", companyId, "expenses"),
        where("status", "==", "Pending"),
      ),
      (snap) => {
        setPendingExpenseCount(snap.size);
      },
    );

    // Attention Needed: tasks (recent, then compute overdue/pending from cache)
    unsubTasks = onSnapshot(
      query(
        collection(db, "companies", companyId, "tasks"),
        orderBy("createdAt", "desc"),
        limit(500),
      ),
      (snap) => {
        tasksCache = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        recomputeAttentionNeeded();
      },
    );

    // ✅ IMPORTANT: missing clock-out depends on time passing even if data doesn’t change
    // so we recompute every 60 seconds using cached data.
    intervalId = setInterval(() => {
      recomputeOperational();
    }, 60 * 1000);

    return () => {
      try {
        unsubCompany?.();
        unsubEmployees?.();
        unsubSchedules?.();
        unsubTimeTracking?.();
        unsubLeave?.();
        unsubExpenses?.();
        unsubTasks?.();
        if (intervalId) clearInterval(intervalId);
      } catch (e) {
        // ignore
      }
    };
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;

    let cancelled = false;

    (async () => {
      try {
        // ✅ assumes employees are stored here:
        // companies/{companyId}/employees
        const employeesCol = collection(
          db,
          "companies",
          companyId,
          "employees",
        );

        const snap = await getCountFromServer(employeesCol);
        const count = snap.data().count;

        if (!cancelled) setTotalEmployees(count);
      } catch (e) {
        console.error("Failed to count employees:", e);
        if (!cancelled) setTotalEmployees(0);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;

    let cancelled = false;

    (async () => {
      try {
        // 1) Load company timezone
        const companySnap = await getDoc(doc(db, "companies", companyId));
        const companyTimeZone = companySnap.data()?.timeZone || "UTC";

        // 2) Compute current week range in company TZ
        const now = DateTime.now().setZone(companyTimeZone);
        const weekStartISO = now.startOf("week").toISODate(); // "YYYY-MM-DD"
        const weekEndISO = now.endOf("week").toISODate(); // "YYYY-MM-DD"

        // 3) Query ONLY entries in this week (fast + accurate with your `date` field)
        const ttRef = collection(db, "companies", companyId, "time_tracking");

        const ttSnap = await getDocs(
          query(
            ttRef,
            where("date", ">=", weekStartISO),
            where("date", "<=", weekEndISO),
          ),
        );

        // 4) Sum hoursWorked
        let total = 0;
        ttSnap.forEach((d) => {
          const row = d.data() || {};
          total += Number(row.hoursWorked || 0);
        });

        // round to 2 decimals
        const rounded = Math.round(total * 100) / 100;

        if (!cancelled) setHoursThisWeek(rounded);
      } catch (e) {
        console.error("Failed to calculate Hours Worked (This Week):", e);
        if (!cancelled) setHoursThisWeek(0);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;

    let cancelled = false;

    (async () => {
      try {
        // 1) Load company timezone + payrollConfig
        const companySnap = await getDoc(doc(db, "companies", companyId));
        const tz = companySnap.data()?.timeZone || "UTC";

        const payrollConfig = companySnap.data()?.payrollConfig || null;
        const periodType = payrollConfig?.frequency || "weekly";
        const anchorISO = payrollConfig?.anchorDate || null;

        // If no anchor, we can’t compute “real” pay periods reliably
        if (!anchorISO) {
          if (!cancelled) {
            setPayrollReadyPct(0);
            setPayrollRangeLabel("Set payroll anchor date");
          }
          return;
        }

        // 2) Compute ACTIVE pay period window
        const startISO = computeActivePayPeriodStart({
          anchorISO,
          periodType,
          tz,
        });

        const endDT = computePayPeriodEndFromStart({
          startISO,
          periodType,
          tz,
        });

        if (!startISO || !endDT) {
          if (!cancelled) {
            setPayrollReadyPct(0);
            setPayrollRangeLabel("");
          }
          return;
        }

        const endISO = endDT.toISODate();
        const label = formatRangeLabel({ startISO, endISO, tz });

        // 3) Query time_tracking in that pay period
        const ttRef = collection(db, "companies", companyId, "time_tracking");
        const ttSnap = await getDocs(
          query(
            ttRef,
            where("date", ">=", startISO),
            where("date", "<=", endISO),
          ),
        );

        // 4) Compute readiness
        let totalRelevant = 0;
        let readyCount = 0;

        ttSnap.forEach((d) => {
          const row = d.data() || {};

          const isPaidLeave =
            row.entryType === "paid-leave" || row.status === "On Leave";

          const hasClockIn = !!row.clockIn;
          const hasClockOut = !!row.clockOut;

          // Denominator: anything that impacts payroll
          if (isPaidLeave || hasClockIn) totalRelevant += 1;

          // Numerator: paid leave OR fully closed work entry (clockIn + clockOut + valid hours)
          if (isPaidLeave || (hasClockIn && hasClockOut)) {
            const hrs = Number(row.hoursWorked);
            const okHours = Number.isFinite(hrs) && hrs >= 0;
            if (okHours) readyCount += 1;
          }
        });

        const pct =
          totalRelevant > 0
            ? Math.round((readyCount / totalRelevant) * 100)
            : 0;

        if (!cancelled) {
          setPayrollReadyPct(pct);
          setPayrollRangeLabel(label ? `Pay period: ${label}` : "");
        }
      } catch (e) {
        console.error("Failed to calculate Payroll-Ready % (pay period):", e);
        if (!cancelled) {
          setPayrollReadyPct(0);
          setPayrollRangeLabel("");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;

    let cancelled = false;

    (async () => {
      try {
        // company TZ
        const companySnap = await getDoc(doc(db, "companies", companyId));
        const tz = companySnap.data()?.timeZone || "UTC";

        const now = DateTime.now().setZone(tz);
        const cutoffISO = now.minus({ days: 30 }).toISODate(); // YYYY-MM-DD

        // employees
        const empSnap = await getDocs(
          query(collection(db, "companies", companyId, "employees")),
        );

        const emps = empSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // hireDate is what you write today; startDate is fallback
        const count = emps.filter((e) => {
          const d = e.hireDate || e.startDate;
          return d && d >= cutoffISO;
        }).length;

        if (!cancelled) setNewHiresCount(count);
      } catch (e) {
        console.error("Failed to calculate new hires:", e);
        if (!cancelled) setNewHiresCount(0);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;

    let cancelled = false;

    (async () => {
      try {
        // 1) Company TZ
        const companySnap = await getDoc(doc(db, "companies", companyId));
        const tz = companySnap.data()?.timeZone || "UTC";

        // 2) "Now" as Firestore Timestamp
        const nowTs = Timestamp.fromDate(
          DateTime.now()
            .setZone(tz)
            .toJSDate(),
        );

        // 3) Employees (Active only, like your Scheduled Today logic)
        const empSnap = await getDocs(
          collection(db, "companies", companyId, "employees"),
        );

        const employees = empSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((e) => e.status === "Active");

        // 4) Count employees with >= 1 overdue training
        let overdueEmployees = 0;

        for (const emp of employees) {
          const progRef = collection(
            db,
            "companies",
            companyId,
            "employees",
            emp.id,
            "training_progress",
          );

          let hasOverdue = false;

          // ✅ Fast path: query for any overdue doc
          try {
            const snap = await getDocs(
              query(
                progRef,
                where("deadline", "<", nowTs),
                where("status", "!=", "completed"),
              ),
            );

            hasOverdue = !snap.empty;
          } catch (qErr) {
            // ✅ Fallback: compute in JS (in case query/index complains)
            const snap = await getDocs(progRef);

            const now = DateTime.now().setZone(tz);

            snap.forEach((docSnap) => {
              if (hasOverdue) return;

              const row = docSnap.data() || {};
              if (row.status === "completed") return;
              if (!row.deadline?.toDate) return;

              const due = DateTime.fromJSDate(row.deadline.toDate()).setZone(
                tz,
              );
              if (due < now) hasOverdue = true;
            });
          }

          if (hasOverdue) overdueEmployees += 1;
        }

        if (!cancelled) setTrainingOverdueCount(overdueEmployees);
      } catch (e) {
        console.error("Failed to calculate training overdue employees:", e);
        if (!cancelled) setTrainingOverdueCount(0);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;

    let cancelled = false;

    (async () => {
      try {
        // 1) Get company TZ (or use your already-loaded companyTimeZone)
        const companySnap = await getDoc(doc(db, "companies", companyId));
        const tz = companySnap.data()?.timeZone || "UTC";

        const todayISO = DateTime.now()
          .setZone(tz)
          .startOf("day")
          .toISODate();

        // 2) Active employee set (so we only count real active employees)
        const empSnap = await getDocs(
          collection(db, `companies/${companyId}/employees`),
        );
        const activeEmpIds = new Set(
          empSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((e) => (e.status || "Active") === "Active")
            .map((e) => e.id),
        );

        // 3) Pull recent tasks (client-side filter to avoid indexes)
        const tasksSnap = await getDocs(
          query(
            collection(db, `companies/${companyId}/tasks`),
            orderBy("createdAt", "desc"),
            limit(500), // adjust if needed
          ),
        );

        const overdueEmpIds = new Set();

        tasksSnap.forEach((docSnap) => {
          const t = docSnap.data() || {};

          const assigneeId =
            t.assigneeId ||
            (Array.isArray(t.assigneeIds) ? t.assigneeIds[0] : null);
          if (!assigneeId) return;

          // only count active employees
          if (!activeEmpIds.has(assigneeId)) return;

          // must have a due date
          if (!t.dueDateISO) return;

          const status = t.status || "open";
          const review = t.reviewStatus || "not_submitted";

          const isOverdue =
            t.dueDateISO < todayISO &&
            review !== "approved" &&
            status !== "archived";

          if (isOverdue) overdueEmpIds.add(assigneeId);
        });

        if (!cancelled) setTaskOverdueCount(overdueEmpIds.size);
      } catch (e) {
        console.error("Task overdue count failed:", e);
        if (!cancelled) setTaskOverdueCount(0);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;

    let cancelled = false;

    (async () => {
      try {
        // Active employees (optional but recommended)
        const empSnap = await getDocs(
          collection(db, `companies/${companyId}/employees`),
        );
        const activeEmpIds = new Set(
          empSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((e) => (e.status || "Active") === "Active")
            .map((e) => e.id),
        );

        // Pull recent tasks (no-index approach)
        const tasksSnap = await getDocs(
          query(
            collection(db, `companies/${companyId}/tasks`),
            orderBy("createdAt", "desc"),
            limit(500),
          ),
        );

        const pendingEmpIds = new Set();

        tasksSnap.forEach((docSnap) => {
          const t = docSnap.data() || {};

          // must be submitted for review
          const review = t.reviewStatus || "not_submitted";
          if (review !== "submitted") return;

          // ignore archived tasks
          const status = t.status || "open";
          if (status === "archived") return;

          // who submitted (single assign model)
          const assigneeId =
            t.assigneeId ||
            (Array.isArray(t.assigneeIds) ? t.assigneeIds[0] : null);

          if (!assigneeId) return;

          // optional: only count active employees
          if (!activeEmpIds.has(assigneeId)) return;

          pendingEmpIds.add(assigneeId);
        });

        if (!cancelled) setTaskPendingApprovalCount(pendingEmpIds.size);
      } catch (e) {
        console.error("Pending approval count failed:", e);
        if (!cancelled) setTaskPendingApprovalCount(0);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // ✅ Placeholder values for now (we will wire real data next)
  const kpis = {
    totalEmployees: totalEmployees ?? "—", // ✅ wired
    scheduledToday: scheduledToday ?? "—",
    hoursThisWeek: hoursThisWeek ?? "—",

    payrollReadyPct: payrollReadyPct ?? "—",
  };

  const openIssuesCount =
    (pendingLeaveCount > 0 ? 1 : 0) +
    (pendingExpenseCount > 0 ? 1 : 0) +
    (trainingOverdueCount > 0 ? 1 : 0) +
    (taskOverdueCount > 0 ? 1 : 0) +
    (taskPendingApprovalCount > 0 ? 1 : 0) +
    (missingClockOutCount > 0 ? 1 : 0) +
    (incompleteProfilesCount > 0 ? 1 : 0);

  return (
    <div className="db-wrap">
      {/* KPI Row */}
      <div className="db-kpi-grid">
        <KpiCard
          icon={Users}
          label="Total Employees"
          value={kpis.totalEmployees}
          iconClass="ico-orange"
        />
        <KpiCard
          icon={CalendarDays}
          label="Scheduled Today"
          value={kpis.scheduledToday}
          iconClass="ico-blue"
        />
        <KpiCard
          icon={Clock4}
          label="Hours Worked"
          value={kpis.hoursThisWeek}
          sub="This Week"
          iconClass="ico-purple"
        />

        <KpiCard
          icon={AlertTriangle}
          label="Open Issues"
          value={openIssuesCount}
          sub="needs attention"
          tone={openIssuesCount > 0 ? "warn" : "neutral"}
          iconClass="ico-amber"
        />

        <KpiCard
          icon={DollarSign}
          label="Payroll-Ready"
          value={`${kpis.payrollReadyPct}%`}
          sub={payrollRangeLabel || "ready for payroll"}
          tone={kpis.payrollReadyPct >= 80 ? "good" : "warn"}
          iconClass="ico-green"
        />
      </div>

      {/* Main Grid */}
      <div className="db-main-grid">
        {/* Attention Needed */}
        <div className="db-card db-card-fixed">
          <div className="db-card-head">
            <div className="db-card-title">
              <AlertTriangle size={16} className="alert" />
              <span>Attention Needed</span>
            </div>
          </div>

          <div className="db-card-body db-card-body-scroll">
            {pendingLeaveCount > 0 && (
              <ListRow
                icon={AlertTriangle}
                iconClass="ico-1"
                text={`${pendingLeaveCount} leave request${
                  pendingLeaveCount === 1 ? "" : "s"
                } pending approval`}
                onClick={() => onTabChange?.("leave")}
              />
            )}

            {pendingExpenseCount > 0 && (
              <ListRow
                icon={AlertTriangle}
                iconClass="ico-1"
                text={`${pendingExpenseCount} expense request${
                  pendingExpenseCount === 1 ? "" : "s"
                } pending approval`}
                onClick={() => onTabChange?.("Expense")}
              />
            )}

            {missingClockOutCount > 0 && (
              <ListRow
                icon={AlertTriangle}
                iconClass="ico-1"
                text={`${missingClockOutCount} employee${
                  missingClockOutCount === 1 ? "" : "s"
                } missing clock-out after scheduled end`}
                onClick={() => onTabChange?.("attendance")}
              />
            )}

            {trainingOverdueCount > 0 && (
              <ListRow
                icon={AlertTriangle}
                iconClass="ico-1"
                text={`${trainingOverdueCount} employee${
                  trainingOverdueCount === 1 ? "" : "s"
                } overdue on training`}
                onClick={() => onTabChange?.("Training")}
              />
            )}
            {incompleteProfilesCount > 0 && (
              <ListRow
                icon={AlertTriangle}
                iconClass="ico-1"
                text={`${incompleteProfilesCount} employee${
                  incompleteProfilesCount === 1 ? "" : "s"
                } have incomplete profiles`}
                onClick={() => onTabChange?.("employee-management")}
              />
            )}

            {taskOverdueCount > 0 && (
              <ListRow
                icon={AlertTriangle}
                iconClass="ico-1"
                text={`${taskOverdueCount} employee${
                  taskOverdueCount === 1 ? "" : "s"
                } overdue on tasks`}
                onClick={() => onTabChange?.("taskmanager")}
              />
            )}

            {taskPendingApprovalCount > 0 && (
              <ListRow
                icon={AlertTriangle}
                iconClass="ico-1"
                text={`${taskPendingApprovalCount} employee${
                  taskPendingApprovalCount === 1 ? "" : "s"
                } have tasks pending approval`}
                onClick={() => onTabChange?.("taskmanager")}
              />
            )}
            {openIssuesCount === 0 && (
              <div className="db-empty">No issues right now </div>
            )}
          </div>
        </div>

        {/* People Overview */}
        <div className="db-card">
          <div className="db-card-head">
            <div className="db-card-title">
              <Users size={16} />
              <span>People Overview</span>
            </div>
          </div>

          <div className="db-card-body">
            <div className="db-mini">
              <div className="db-mini-label">New Hires</div>
              <div className="db-mini-value">{newHiresCount ?? "—"}</div>
              <div className="db-mini-sub">Last 30 days</div>
            </div>

            <div className="db-mini">
              <div className="db-mini-label">Incomplete Profiles</div>
              <div className="db-mini-value">
                {incompleteProfilesCount ?? "—"}
              </div>
              <div className="db-mini-sub">Missing information</div>
            </div>

            <button
              className="db-primary"
              type="button"
              onClick={() => onTabChange?.("employee-management")}
            >
              Manage Employees
            </button>
          </div>
        </div>

        {/* Payroll Readiness */}
        <div className="db-card">
          <div className="db-card-head">
            <div className="db-card-title">
              <CalendarDays size={16} />
              <span>Operational Snapshot</span>
            </div>
          </div>

          <div className="db-card-body">
            <div className="ops-wrap">
              <div className="ops-section">
                <div className="ops-row">
                  <div className="ops-label">Coverage Today</div>
                  <div className="ops-sub">
                    {DateTime.now()
                      .setZone(companyTz || "UTC")
                      .toFormat("h:mm a • MMM dd, yyyy • ZZZZ")}
                  </div>
                </div>

                <div className="ops-metrics">
                  <div className="ops-metric">
                    <div className="ops-metric-value">
                      {scheduledToday ?? "—"}
                    </div>
                    <div className="ops-metric-label">employees scheduled</div>
                  </div>

                  <div className="ops-metric">
                    <div className="ops-metric-value">
                      {clockedInToday ?? "—"}
                    </div>
                    <div className="ops-metric-label">currently clocked in</div>
                  </div>
                  <div className="ops-metric">
                    <div className="ops-metric-value">
                      {clockInOnTimeCount ?? "—"}
                    </div>
                    <div className="ops-metric-label">on-time clock-ins</div>
                  </div>

                  <div className="ops-metric">
                    <div className="ops-metric-value">
                      {clockInLateCount ?? "—"}
                    </div>
                    <div className="ops-metric-label">late clock-ins</div>
                  </div>

                  <div className="ops-metric">
                    <div className="ops-metric-value">
                      {callOutTodayCount ?? "—"}
                    </div>
                    <div className="ops-metric-label">call-outs</div>
                  </div>

                  <div className="ops-metric">
                    <div className="ops-metric-value">
                      {clockInEarlyCount ?? "—"}
                    </div>
                    <div className="ops-metric-label">early clock-ins</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="db-card">
          <div className="db-card-head">
            <div className="db-card-title">
              <ChevronRight size={16} />
              <span>Quick Actions</span>
            </div>
          </div>

          <div className="db-card-body">
            <button
              className="db-action"
              type="button"
              onClick={() => onTabChange?.("employee-management")}
            >
              Add Employee
            </button>
            <button
              className="db-action"
              type="button"
              onClick={() => onTabChange?.("Scheduling")}
            >
              Create Schedule
            </button>
            <button
              className="db-action"
              type="button"
              onClick={() => onTabChange?.("attendance")}
            >
              Review Time Logs
            </button>
            <button
              className="db-action"
              type="button"
              onClick={() => onTabChange?.("Payroll")}
            >
              Run payroll Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
